import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { banco } from '../../db/banco'
import CartaoErro from '../../componentes/CartaoErro'
import { montarDadosPedido, type DadosPedidoPdf } from '../../pedidos/pdfDados'
import { marcasParaFabrica } from '../../pedidos/agrupamento'
import { caminhoDaPrevia } from '../../pedidos/alvoDoEnvio'
import { contatoDoRepresentante } from '../../lib/contatoDoRepresentante'

export default function EnviarPedido() {
  const { id } = useParams()
  const navegar = useNavigate()
  const { session } = useAuth()
  const [dados, setDados] = useState<DadosPedidoPdf | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  // ⚠ AO VIVO, de proposito. O selo "enviado" muda em OUTRA rota (a previa) e
  // tambem chega pela sincronizacao. Um Set em memoria mostraria "aguardando
  // envio" num sub-pedido que acabou de ir -- e leitura unica mente em aparelho
  // recem-aberto, que ja mordeu quatro telas deste projeto.
  //
  // Estreito de proposito: so sub_pedidos. Envolver montarDadosPedido inteiro
  // seria tentador e errado -- ele LANCA quando o pedido nao existe, e erro
  // dentro do querier estoura na renderizacao, matando o caminho de erro abaixo.
  const statusPorSubPedido = useLiveQuery(async () => {
    if (!id) return new Map<string, string>()
    const subs = await banco.sub_pedidos.where('pedido_id').equals(id).toArray()
    return new Map(subs.map((s) => [s.id, s.status]))
  }, [id])

  useEffect(() => {
    if (!id) return
    montarDadosPedido(id, contatoDoRepresentante(session?.user), session?.user.id)
      .then(setDados)
      .catch((excecao) =>
        setErro(excecao instanceof Error ? excecao.message : 'Erro ao montar o pedido.'),
      )
  }, [id, session])

  if (erro) return <section className="p-4"><CartaoErro titulo="Não foi possível preparar o pedido">{erro}</CartaoErro></section>
  if (!dados) return <p className="p-4 text-sm text-slate-500">Preparando os documentos…</p>

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Enviar pedido {dados.pedido.numero}</h2>
      <p className="text-sm text-slate-500">
        Confira cada documento antes de mandar. O arquivo só sai depois que você conferir.
      </p>

      <div className="bg-white rounded-xl shadow-sm divide-y">
        <div className="p-3 flex items-center justify-between">
          <div>
            <span className="block text-sm font-medium text-slate-800">Resumo para o cliente</span>
            <span className="block text-xs text-slate-500">Todas as fornecedoras juntas</span>
          </div>
          <button
            data-testid="conferir-consolidado"
            onClick={() => navegar(caminhoDaPrevia(id!, 'consolidado'))}
            className="bg-teal-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            Conferir e enviar
          </button>
        </div>
        {marcasParaFabrica(dados).map((marca) => {
          // Sem a leitura ao vivo ainda pronta, cai no status do snapshot -- que
          // e o que a tela ja mostrava antes. Nunca "aguardando" por engano.
          const enviada = marca.tabelas.every((t) => {
            const status = statusPorSubPedido?.get(t.subPedido.id) ?? t.subPedido.status
            return status !== 'fechado'
          })
          return (
            <div key={marca.representadaId} className="p-3 flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-slate-800">
                  {marca.numero} — {marca.representada.nome}
                </span>
                <span className="block text-xs text-slate-500">
                  {enviada ? '✓ enviado' : 'aguardando envio'}
                  {marca.tabelas.length > 1 && ` — ${marca.tabelas.length} tabelas`}
                </span>
              </div>
              <button
                data-testid={`conferir-${marca.letra}`}
                onClick={() => navegar(caminhoDaPrevia(id!, { letra: marca.letra }))}
                className="bg-teal-700 text-white rounded-lg px-3 py-2 text-sm"
              >
                Conferir e enviar
              </button>
            </div>
          )
        })}
      </div>

      <button
        onClick={() => navegar('/pedidos')}
        className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
      >
        Concluir
      </button>
    </section>
  )
}
